import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../api/auth-api.js";
import { isApiError } from "../../api/client.js";
import { acceptInvitation } from "../../api/team-api.js";
import { Button } from "../../components/ui/button/index.js";
import { useAuth } from "../auth/auth-context.js";
import { AuthAlert, AuthEntryHeader, AuthEntryShell, GoogleSignInButton } from "../auth/entry/index.js";
import { AUTH_ENTRY_PRIMARY_ACTION } from "../auth/entry/auth-entry-styles.js";
import { prefersOauthRedirect } from "../auth/prefer-oauth-redirect.js";
import { startGoogleAuthentication } from "../auth/start-google-authentication.js";
import { useGooglePopup } from "../auth/use-google-popup.js";
import { useOrganizations } from "../organizations/organization-context.js";

function inviteToken(): string {
  return new URLSearchParams(window.location.search).get("token")?.trim() ?? "";
}

export function InvitationAcceptPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { phase, retry, setAuthenticating, adoptSessionUser } = useAuth();
  const { select } = useOrganizations();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const token = inviteToken();

  const { status, start, reset } = useGooglePopup(() => {
    setAuthenticating();
    retry();
  });

  async function accept(): Promise<void> {
    if (token.length < 20) {
      setError(t("team.invite.invalidToken"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await acceptInvitation(token);
      const user = await getCurrentUser();
      adoptSessionUser(user);
      select(result.organizationId);
      navigate("/inbox", { replace: true });
    } catch (caught) {
      setError(isApiError(caught) ? caught.message : t("team.invite.acceptError"));
    } finally {
      setSubmitting(false);
    }
  }

  const authenticated = phase.kind === "authenticated";

  return (
    <AuthEntryShell>
      <AuthEntryHeader title={t("team.invite.acceptTitle")} subtitle={t("team.invite.acceptBody")} />
      {error ? <AuthAlert>{error}</AuthAlert> : null}
      {authenticated ? (
        <Button
          className={AUTH_ENTRY_PRIMARY_ACTION}
          onClick={() => void accept()}
          disabled={submitting}
          data-testid="invitation-accept"
        >
          {submitting ? t("team.invite.accepting") : t("team.invite.accept")}
        </Button>
      ) : (
        <div className="grid gap-3">
          {status.kind === "blocked" ? (
            <AuthAlert
              actions={
                <Button
                  variant="link"
                  onClick={() => {
                    startGoogleAuthentication({
                      preferRedirect: true,
                      startPopup: start,
                      returnPath: `${window.location.pathname}${window.location.search}`,
                    });
                  }}
                >
                  {t("auth.popupBlocked.redirect")}
                </Button>
              }
            >
              {t("auth.popupBlocked.message")}
            </AuthAlert>
          ) : null}
          {status.kind === "error" ? (
            <AuthAlert actions={<Button variant="link" onClick={reset}>{t("auth.error.retry")}</Button>}>
              {t("auth.error.message", { code: status.code })}
            </AuthAlert>
          ) : null}
          <GoogleSignInButton
            label={t("auth.continueWithGoogle")}
            loadingLabel={t("auth.opening")}
            loading={status.kind === "opening"}
            disabled={status.kind === "opening"}
            onClick={() => {
              startGoogleAuthentication({
                preferRedirect: prefersOauthRedirect(),
                startPopup: start,
                returnPath: `${window.location.pathname}${window.location.search}`,
              });
            }}
          />
        </div>
      )}
    </AuthEntryShell>
  );
}
